import { useCallback } from 'react';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Custom hook for GTM (Google Tag Manager) tracking in Ampol Cash Order Modal
 *
 * Centralizes all analytics tracking for the modal workflow:
 * - Modal open/close events
 * - Step navigation events
 * - Order submission events
 *
 * @param loggedInUser - Current user from Redux
 * @param assignedAccountNumber - Account number from Redux
 * @returns GTM tracking functions
 */
export const useAmpolCashOrderGTM = (loggedInUser, assignedAccountNumber) => {
  /**
   * Track modal close event
   */
  const trackCloseModal = useCallback(() => {
    trackCustomEvent('Close Cash Order Modal', loggedInUser, assignedAccountNumber, 'Cash Order Modal closed.');
  }, [loggedInUser, assignedAccountNumber]);

  /**
   * Track navigation to delivery options
   */
  const trackContinueToDeliveryOptions = useCallback(() => {
    trackCustomEvent(
      'Continue to Delivery Options',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Continue button to go to Delivery Options.'
    );
  }, [loggedInUser, assignedAccountNumber]);

  /**
   * Track navigation to summary page
   */
  const trackContinueToSummary = useCallback(() => {
    trackCustomEvent(
      'Continue to Summary Page',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Continue button to go to Summary page on Cash Order modal.'
    );
  }, [loggedInUser, assignedAccountNumber]);

  /**
   * Track order placement
   */
  const trackPlaceOrder = useCallback(() => {
    trackCustomEvent(
      'Submit Place Order',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Place Order button to submit Cash Order.'
    );
  }, [loggedInUser, assignedAccountNumber]);

  /**
   * Track navigation to order placed page
   */
  const trackOrderPlaced = useCallback(() => {
    trackCustomEvent(
      'Continue to Order Placed Page',
      loggedInUser,
      assignedAccountNumber,
      'Navigated to Order Placed page after successful submission on Cash Order modal.'
    );
  }, [loggedInUser, assignedAccountNumber]);

  return {
    trackCloseModal,
    trackContinueToDeliveryOptions,
    trackContinueToSummary,
    trackPlaceOrder,
    trackOrderPlaced,
  };
};
